import { MdError } from 'react-icons/md';
import { Button, Divider, ModalBody, ModalContent, ModalHeader, Spinner, Tab, Tabs, useDisclosure } from '@ds';
import { DrawerModal } from '@ui';
import { BaseViolationItem } from '@lp/features/Scheduler/types';
import { DrawerViolationCard } from './components';

type BaseValidationDataFields = {
  isFetching: boolean;
  count: number;
};

type ConflictsDrawerModalProps<A extends string, V extends BaseViolationItem, H extends Record<string, unknown>> = {
  tabsData: {
    label: string;
    accessorKey: A;
    ignoreDay?: boolean;
    transformerFn?: (payload: V) => H;
  }[];
  validationData?: Record<A, V[]> & BaseValidationDataFields;
  onItemClick?: (payload: H) => void;
};

export const ConflictsDrawerModal = <A extends string, V extends BaseViolationItem, H extends Record<string, unknown>>({
  tabsData,
  validationData,
  onItemClick,
}: ConflictsDrawerModalProps<A, V, H>) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (!validationData) return null;

  return (
    <>
      <Button
        variant="light"
        size="md"
        disableRipple
        startContent={validationData.isFetching ? <Spinner size="sm" color="danger" /> : <MdError size={20} />}
        className="bg-danger-70 text-danger-40!"
        onPress={onOpen}
      >
        Warnings: {validationData.count}
      </Button>
      <DrawerModal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        className="bg-base-80"
        classNames={{
          closeButton: 'text-base-20 m-4 text-hl top-[-10px]',
        }}
      >
        <ModalContent>
          <ModalHeader className="pb-0">
            <h3 className="text-hl text-base-20">Warnings</h3>
          </ModalHeader>
          <ModalBody>
            <Divider />
            <Tabs
              size="sm"
              variant="underlined"
              classNames={{
                tabList: 'gap-0 w-full px-0',
                panel: 'h-full overflow-auto',
              }}
            >
              {tabsData.map((tab) => {
                const errorsList = validationData[tab.accessorKey];
                const { transformerFn } = tab;

                return (
                  <Tab key={tab.label} title={tab.label} className="px-0" isDisabled={errorsList.length === 0}>
                    <div className="flex gap-3 flex-col">
                      {errorsList.flatMap((item, itemIndex) => {
                        const day = tab.ignoreDay ? undefined : item.day;
                        const clickHandler = transformerFn ? () => onItemClick?.(transformerFn(item)) : undefined;

                        return item.messages.map((m, mIndex) => (
                          <DrawerViolationCard
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${itemIndex}-${mIndex}`}
                            title={item.title}
                            message={m}
                            day={day}
                            onClick={clickHandler}
                          />
                        ));
                      })}
                    </div>
                  </Tab>
                );
              })}
            </Tabs>
          </ModalBody>
        </ModalContent>
      </DrawerModal>
    </>
  );
};
